import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import '../../fonts.css'
import { useMapRef, useMapStore } from "../../store/useMapStore.js";
import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import { useSearchParams } from "react-router-dom";
import { getRuntimeConfig } from "../../runtime-config.js";

const config = getRuntimeConfig();


const GeocodingControl = ({ mapRef, show, onAddMarker}) => {

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { instance, accounts } = useMsal();

  // Edit functionality states
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [tempCoordinates, setTempCoordinates] = useState(null);
  
  // Saved location data
  const [savedLocation, setSavedLocation] = useState({
    text: "",
    coordinates: null
  });
  
  const mapref = useMapRef(state=> state.mapref)
  const updateLocation= useMapStore((state)=>state.updateLocation);
  const useCurrentLocation= useMapStore((state)=>state.currentLocation);
  const useHome = useMapStore((state)=> state.home)
  const updateHome = useMapStore((state)=> state.updateHome)
  const updateHomeCoordinates = useMapStore((state)=> state.updateHomeCoordinates);
  const useHomeCoordinates = useMapStore((state)=> state.homeCoordinates)
  const azure_maps_id = config.AZ_MAPS_CLIENTID;

  // Handle search input changes
  const handleSearchChange = (e) => {
    if (isEditMode) {
      // If in edit mode, update temp value and perform search
      setTempValue(e.target.value);
      if (e.target.value.length > 2) {
        performSearch(e.target.value);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } else {
      // Normal search functionality
      setSearchQuery(e.target.value);
      if (e.target.value.length > 2) {
        performSearch(e.target.value);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }
  };

  // Edit functionality methods
  const startEdit = () => {
    setIsEditMode(true);
    setTempValue(useCurrentLocation);
    setTempCoordinates(null);
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setTempValue("");
    setTempCoordinates(null);
  };

  const saveEdit = () => {
    // Save both text and coordinates
    const locationData = {
      text: tempValue,
      coordinates: tempCoordinates
    };
    setSavedLocation(locationData);
    updateLocation(tempValue);
    setIsEditMode(false);
    setTempValue("");
    setTempCoordinates(null);
    updateHomeCoordinates(coordinates);
  };

const performSearch = async (query) => {
  if (!query || query.length < 3) return;
 
  setIsSearching(true);
  try {
   
    const accounts = instance.getAllAccounts();
    const request = {
      scopes: ["https://atlas.microsoft.com/user_impersonation"],
      account: accounts[0]
    };
    const requestresponse = await instance.acquireTokenSilent(request);
    
    const authHeaders = {
      Authorization: `Bearer ${requestresponse.accessToken}`,
      "x-ms-client-id": azure_maps_id
    };

    // --- New geocoding API (2023-06-01): better address coverage ---
    const searchGeocodeNew = async (searchText, useBbox) => {
      const params = new URLSearchParams({
        "api-version": "2023-06-01",
        query: searchText,
        countryRegion: "DE,NL",
        top: "10"
      });
      if (useBbox) {
      
        params.set("bbox", "3.36,47.27,15.04,55.06");
      }
      const response = await fetch(
        `https://atlas.microsoft.com/geocode?${params.toString()}`,
        { method: "GET", headers: authHeaders }
      );
      if (!response.ok) return null;
      const json = await response.json();
      // New API returns GeoJSON FeatureCollection
      return (json.features ?? []).map(f => ({
        lat: f.geometry.coordinates[1],
        lon: f.geometry.coordinates[0],
        display_name: f.properties.address?.formattedAddress ?? searchText,
        address: f.properties.address ?? {},
        type: f.properties.type,
        _azure_original: f
      }));
    };

    // --- Legacy API (1.0) fallback ---
    const searchMaps = async (endpoint, searchText, useBounds, extraParams = {}) => {
      const params = new URLSearchParams({
        "api-version": "1.0",
        query: searchText,
        countrySet: "DE,NL",
        limit: "10",
        language: "de-DE",
        ...extraParams
      });
      if (useBounds) {
        params.set("topLeft", "55.06,3.36");
        params.set("btmRight", "47.27,15.04");
      }
      const response = await fetch(
        `https://atlas.microsoft.com/search/${endpoint}/json?${params.toString()}`,
        { method: "GET", headers: authHeaders }
      );
      if (!response.ok) {
        console.log("Azure maps response: ", response);
        throw new Error(`Azure Maps API error: ${response.status}`);
      }
      const json = await response.json();
      return (json.results ?? []).map(result => ({
        lat: result.position.lat,
        lon: result.position.lon,
        display_name: result.address.freeformAddress,
        address: result.address,
        type: result.type,
        _azure_original: result
      }));
    };

    const normalizedQuery = query.replace(/ß/g, "ss");

    
    let transformedResults = await searchGeocodeNew(query, true);
    // 2. New API without bbox
    if (!transformedResults?.length) transformedResults = await searchGeocodeNew(query, false);
    // 3. New API with ß normalised
    if (!transformedResults?.length && normalizedQuery !== query) {
      transformedResults = await searchGeocodeNew(normalizedQuery, false);
    }
    // 4. Legacy address endpoint
    if (!transformedResults?.length) transformedResults = await searchMaps("address", query, true);
    if (!transformedResults?.length) transformedResults = await searchMaps("address", query, false);
    // 5. Legacy fuzzy endpoint
    if (!transformedResults?.length) {
      transformedResults = await searchMaps("fuzzy", query, false, {
        idxSet: "PAD,Addr,Str",
        minFuzzyLevel: "1",
        maxFuzzyLevel: "3"
      });
    }

    transformedResults = transformedResults ?? [];
   
    //const transformedResults = AzureMapsSearch(msalInstance, query, azure_maps_id)
    //console.log(transformedResults)
    setSearchResults(transformedResults);
    setShowResults(true);
  } catch (error) {
    console.error("Geocoding error:", error);
    setSearchResults([]);
  } finally {
    setIsSearching(false);
  }
};
  // selecting the location result from the dropdown list
const handleSelectLocation = (result) => {
    const locationName = result.display_name.split(',')[0];
    const coordinates = {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon)
    };
    
    if (show) {
      setSearchQuery(locationName);
      updateLocation(result.display_name);
      
      if (mapRef && mapRef.current && mapRef.current.getMap) {
        const map = mapRef.current.getMap();
        
        map.flyTo({
          center: [coordinates.lon, coordinates.lat],
          zoom: 18,
          essential: true
        });
        map.once('moveend', () => {
          onAddMarker(coordinates);
        });
        
     
      }
    } else if (isEditMode) {
      setTempValue(result.display_name);
      setTempCoordinates(coordinates);
      updateHome(result.display_name);
      updateHomeCoordinates(coordinates);
    }    
    
    setSearchResults([]);
    setShowResults(false);
  };

  const handleHomeClick = () => {
      if (!mapref?.current) return;

      const map = mapref.current.getMap();
      

      // Normalize longitude
      const lng =
        useHomeCoordinates.lng ??
        useHomeCoordinates.lon;

      const { lat } = useHomeCoordinates;

      // Guard against missing coordinates
      if (lng == null || lat == null) {
        console.warn("Invalid home coordinates", useHomeCoordinates);
        return;
      }
     

      map.flyTo({
        center: [lng, lat],
        zoom: 14,
        essential: true
      });
      onAddMarker(useHomeCoordinates);
      setSearchParams(
        prev => {
          prev.set("lng", lng.toFixed(4));
          prev.set("lat", lat.toFixed(4));
          prev.set("zoom", 14);
          return prev;
        },
        { replace: true }
      );

      updateLocation(useHome);
    };


  const baseStyle = {
    width: "310px",
    height: "40px",
    paddingTop: "6px",
    paddingRight: "8px",
    paddingLeft: "45px",
    fontSize: "14px",
    borderRadius: "40px",
    border: "1px solid #ccc",
    backgroundColor: "white",
    color: "#AAADB0",
    fontFamily: "GoldbeckNextRegular",
    outline:"none"
  };

  const showStyle = {
    borderRadius: "40px",
  };

  const hideStyle = {
    borderRadius: "8px",
    width: "543px",
  };

  // Get current input value
  const getCurrentValue = () => {
    if (isEditMode) {
      return tempValue;
    }
    return show ? searchQuery : useCurrentLocation;
  };

  return (
    <div 
      style={{
        position: "absolute",
        top: "25px",
        left: "24px",
        transform: "translateX(px-50%)", // Center the control
        zIndex: 1,
        width: "300px"
      }}
    >
      {show?(
        <div
          style={{
            backgroundColor: "white",
            paddingTop: "12px",
            paddingRight:"8px",
            paddingLeft:"20px",
            borderRadius: "12px",
            paddingBottom:"15px",
            marginBottom:"20px",
            gap:"12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            width:"336px",
            height:"122px",
            left:"24px",
            zIndex: 0,
            top:"84px"
          }}
        > 
          <h2 style={{ fontSize: "18px", fontWeight: "500", marginBottom: "o4px", color:"black", fontFamily:"GoldbeckNextSemiBold2"}}>Auswahl</h2>
          <p style={{ fontSize: "14px", color: "#666",fontFamily:"GoldbeckNextRegular" }}>
            Wähle den Ort aus, den du überprüfen möchtest oder navigiere dich zu deinem bevorzugten Standort.
          </p>
          <button 
            aria-label="Home"
            onClick={handleHomeClick}
            style={{ 
              position:"absolute",
              color: '#4a5568', 
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              outline:"none",
              borderRadius: '9999px',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10,
              left:"380px",
              top:"4px",
              height:"40px",
              width:"40px"
            }}
            onMouseOver={(e) => {e.currentTarget.style.backgroundColor = '#EEEFF0'; e.currentTarget.style.color = '#1a202c';}}
            onMouseOut={(e) => {e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = '#4a5568';}}
          >
            <HomeFilledIcon 
              style={{ width: '20px', height: '20px' }} 
            />
          </button>
      </div>
      ) : <></>}

      <div style={{ position: "relative" }}>
        <img
          src="\assets\search.png"
          style={{
            position:"absolute",
            left:"24px",
            paddingTop:"20px"
          }}
        />
        <input
          type="text"
          value={getCurrentValue()}
          onChange={handleSearchChange}
          placeholder={isEditMode ? "Bearbeiten..." : "Suche"}
          style={{
            ...baseStyle,
            ...(show ? showStyle : hideStyle),
            backgroundColor: isEditMode ? '#D5D6D8' : 'white',
            border: isEditMode ? '2px solid #95999D' : '1px solid #ccc'
          }}
        />

        {/* Edit Controls - Only show when show is false */}
        {!show && (
          <div style={{
            position: "absolute",
            right: "-120%",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: "5px"
          }}>
            {!isEditMode ? (
              <button
                onClick={startEdit}
                style={{               
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#3EBAEC",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontFamily: "GoldbeckNextRegular",
                  outline:"none",
                 
                }}
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={saveEdit}
                  style={{
                    marginRight:"-0px",
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    backgroundColor: "#56BA72",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontFamily: "GoldbeckNextRegular",
                    outline:"none",
                   
                  }}
                >
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  style={{
                    marginRight:"-60px",
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    backgroundColor: "#DD5C64",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontFamily: "GoldbeckNextRegular",
                    outline:"none",
                  
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        )}

        {/* Debug info - remove this in production
        {!show && savedLocation.text && (
          <div style={{
            position: "absolute",
            top: "45px",
            left: "0",
            fontSize: "10px",
            color: "#666",
            fontFamily: "GoldbeckNextRegular"
          }}>
            Saved: {savedLocation.text} ({savedLocation.coordinates?.lat.toFixed(4)}, {savedLocation.coordinates?.lon.toFixed(4)})
          </div>
        )} */}

        {isSearching && !isEditMode && (
          <div style={{ position: "absolute", right: "10px", top: "8px" }}>
            Loading...
          </div>
        )}
      </div>
      
      {showResults && searchResults.length > 0 && (
        <div 
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginTop: "4px",
            maxHeight: "300px",
            overflowY: "auto",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1001
          }}
        >
          {searchResults.map((result, index) => (
            <div
              key={index}
              onClick={() => handleSelectLocation(result)}
              style={{
                padding: "8px 12px",
                borderBottom: index < searchResults.length - 1 ? "1px solid #eee" : "none",
                cursor: "pointer",
                fontSize: "14px",
                color: "black",
                outline:"none",
                border:"none"
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              <div style={{ fontWeight: "bold" }}>{result.display_name.split(',')[0]}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {result.display_name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
   
  );
};

export default GeocodingControl;